// Detectar automáticamente la URL del API según el entorno
const API_BASE_URL = import.meta.env.PROD
  ? '/api'  // En producción, usar rutas relativas (mismo dominio de Vercel)
  : 'http://localhost:3001/api'  // En desarrollo, usar localhost

const getAdminAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('admin_token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// Tipos para las respuestas de la API
export interface Lead {
  id: string
  name?: string
  email: string
  phone: string
  created_at: string
  source: string
}

export interface CreateLeadRequest {
  name?: string
  email: string
  phone: string
  // FASE 3A — Dirección Colombia
  tipo_documento?: string
  numero_documento?: string
  departamento?: string
  municipio?: string
  barrio?: string
  tipo_via?: string
  numero_via?: string
  cuadrante?: string
  numero_cruce?: string
  numero_puerta?: string
  complemento?: string
  auth_provider?: 'form' | 'google'
  google_user_id?: string
}

export interface ApiResponse<T = any> {
  success?: boolean
  message?: string
  data?: T
  leads?: T  // Para respuestas de leads
  user?: any  // Para respuestas de auth
  token?: string  // Para respuestas de auth
  error?: string
}

// Tipos para imágenes de colecciones
export interface CollectionImage {
  id: number
  collection: string
  image_url?: string
  storage_path?: string
  full_image_url?: string
  alt_text: string
  order_index: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ImageStats {
  total: number
  by_collection: {
    boys: number
    girls: number
  }
}

// Tipos para videos
export interface StoreVideo {
  id: number
  title: string
  description: string
  video_url: string
  storage_path?: string
  is_active: boolean
  created_at: string
  updated_at?: string
}

export interface VideoAdmin extends StoreVideo {
  // Propiedades adicionales para admin si se necesitan
}

// Cliente API para leads
export const leadsApi = {
  // Crear nuevo lead
  async create(lead: CreateLeadRequest): Promise<ApiResponse<Lead>> {
    try {
      const response = await fetch(`${API_BASE_URL}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(lead),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al crear lead')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en leadsApi.create:', error)
      throw error
    }
  },

  // Obtener todos los leads (solo para admins)
  async getAll(): Promise<ApiResponse<Lead[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/leads`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener leads')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en leadsApi.getAll:', error)
      throw error
    }
  },

  // Obtener estadísticas de leads
  async getStats(): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/leads/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener estadísticas')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en leadsApi.getStats:', error)
      throw error
    }
  }
}

// Cliente API para contenido
export const contentApi = {
  // Obtener todo el contenido
  async getAll(forceReload = false): Promise<any> {
    try {
      const url = forceReload 
        ? `${API_BASE_URL}/content?t=${Date.now()}` // Agregar timestamp para evitar caché
        : `${API_BASE_URL}/content`
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(forceReload && { 'Cache-Control': 'no-cache' }), // Forzar sin caché
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener contenido')
      }

      const result = await response.json()

      // Si la respuesta tiene la estructura { success: true, data: content }
      if (result.success && result.data) {
        return result.data
      }

      // Si la respuesta es directamente el contenido
      return result
    } catch (error) {
      console.error('Error en contentApi.getAll:', error)
      throw error
    }
  },

  // Actualizar contenido (solo para admins)
  async update(section: string, content: any): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/content/${section}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al actualizar contenido')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en contentApi.update:', error)
      throw error
    }
  },

}

// Cliente API para autenticación
export const authApi = {
  // Login
  async login(email: string, password: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error en login')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en authApi.login:', error)
      throw error
    }
  },

  // Verificar token
  async verify(token: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al verificar token')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en authApi.verify:', error)
      throw error
    }
  },

  // Logout
  async logout(): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error en logout')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en authApi.logout:', error)
      throw error
    }
  }
}

// Cliente API para imágenes de colecciones
export const imagesApi = {
  // Obtener imágenes de una colección
  async getByCollection(collection: 'boys' | 'girls'): Promise<ApiResponse<CollectionImage[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/images/${collection}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener imágenes')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en imagesApi.getByCollection:', error)
      throw error
    }
  },

  // Subir nueva imagen
  async upload(file: File, collection: 'boys' | 'girls', altText: string, orderIndex: number): Promise<ApiResponse<CollectionImage>> {
    try {
      const formData = new FormData()
      formData.append('image', file)
      formData.append('collection', collection)
      formData.append('alt_text', altText)
      formData.append('order_index', orderIndex.toString())

      const response = await fetch(`${API_BASE_URL}/images/upload`, {
        method: 'POST',
        headers: { ...getAdminAuthHeaders() },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al subir imagen')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en imagesApi.upload:', error)
      throw error
    }
  },

  // Actualizar imagen
  async update(id: number, updates: Partial<CollectionImage>): Promise<ApiResponse<CollectionImage>> {
    try {
      const response = await fetch(`${API_BASE_URL}/images/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(),
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al actualizar imagen')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en imagesApi.update:', error)
      throw error
    }
  },

  // Eliminar imagen
  async delete(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/images/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(),
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al eliminar imagen')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en imagesApi.delete:', error)
      throw error
    }
  },

  // Obtener estadísticas de imágenes
  async getStats(): Promise<ApiResponse<ImageStats>> {
    try {
      const response = await fetch(`${API_BASE_URL}/images/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener estadísticas')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en imagesApi.getStats:', error)
      throw error
    }
  }
}

// Cliente API para videos de la tienda
export const videosApi = {
  // Obtener video activo de la tienda
  async getStoreVideo(): Promise<ApiResponse<StoreVideo>> {
    try {
      const response = await fetch(`${API_BASE_URL}/videos/store`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener video')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en videosApi.getStoreVideo:', error)
      throw error
    }
  },

  // Subir nuevo video
  async uploadVideo(file: File, title: string, description: string): Promise<ApiResponse<StoreVideo>> {
    try {
      const formData = new FormData()
      formData.append('video', file)
      formData.append('title', title)
      formData.append('description', description)

      const response = await fetch(`${API_BASE_URL}/videos/upload`, {
        method: 'POST',
        headers: { ...getAdminAuthHeaders() },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al subir video')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en videosApi.uploadVideo:', error)
      throw error
    }
  },

  // Eliminar video
  async deleteVideo(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/videos/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(),
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al eliminar video')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en videosApi.deleteVideo:', error)
      throw error
    }
  },

  // Obtener todos los videos (para admin)
  async getAllVideosAdmin(): Promise<ApiResponse<VideoAdmin[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/videos/admin/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(),
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener videos')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en videosApi.getAllVideosAdmin:', error)
      throw error
    }
  }
}

// Tipos para productos
export interface Product {
  id: string
  name: string
  description?: string
  collection: 'boys' | 'girls' | 'all'
  price_cop: number
  image_url?: string | null
  storage_path?: string | null
  full_image_url?: string
  available_sizes: string[]
  stock_by_size: Record<string, number>
  is_active: boolean
  order_index: number
  created_at: string
  updated_at?: string
  extra_images?: ProductImage[]
}

// Tipo para imágenes adicionales de producto
export interface ProductImage {
  id: string
  product_id: string
  storage_path: string
  full_url: string
  is_primary: boolean
  order_index: number
  created_at: string
}

// Respuesta paginada del admin
export interface PaginatedProductsResponse {
  success: boolean
  data: Product[]
  total: number
  page: number
  limit: number
}

// Cliente API para productos
export const productsApi = {
  // Obtener todos los productos activos (público)
  async getAll(): Promise<ApiResponse<Product[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/products`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener productos')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.getAll:', error)
      throw error
    }
  },

  // Obtener siguiente valor de order_index para nuevo producto
  async getNextOrder(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/next-order`, {
        headers: { ...getAdminAuthHeaders() },
      })
      const data = await response.json()
      return data?.data?.next_order ?? 1
    } catch {
      return 1
    }
  },

  // Obtener todos los productos (admin) con paginación opcional
  async getAllAdmin(page?: number, limit?: number): Promise<PaginatedProductsResponse> {
    try {
      const params = page !== undefined && limit !== undefined
        ? `?page=${page}&limit=${limit}`
        : ''
      const response = await fetch(`${API_BASE_URL}/products/admin/all${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAdminAuthHeaders() },
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al obtener productos')
      }
      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.getAllAdmin:', error)
      throw error
    }
  },

  // Crear nuevo producto con imagen (XHR para barra de progreso)
  upload(
    file: File | undefined,
    name: string,
    description: string,
    collection: 'boys' | 'girls' | 'all',
    price_cop: number,
    available_sizes: string[],
    stock_by_size: Record<string, number>,
    order_index: number,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<Product>> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      if (file) formData.append('image', file)
      formData.append('name', name)
      formData.append('description', description)
      formData.append('collection', collection)
      formData.append('price_cop', price_cop.toString())
      formData.append('available_sizes', JSON.stringify(available_sizes))
      formData.append('stock_by_size', JSON.stringify(stock_by_size))
      formData.append('order_index', order_index.toString())

      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${API_BASE_URL}/products`)

      const adminToken = localStorage.getItem('admin_token')
      if (adminToken) xhr.setRequestHeader('Authorization', `Bearer ${adminToken}`)

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText)
          if (xhr.status >= 200 && xhr.status < 300) resolve(data)
          else reject(new Error(data.error || 'Error al crear producto'))
        } catch {
          reject(new Error('Respuesta inválida del servidor'))
        }
      }
      xhr.onerror = () => reject(new Error('Error de red'))
      xhr.send(formData)
    })
  },

  // Subir imagen adicional a un producto (XHR para barra de progreso)
  uploadExtraImage(
    productId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<ApiResponse<ProductImage>> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('image', file)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${API_BASE_URL}/products/${productId}/images`)

      const adminToken = localStorage.getItem('admin_token')
      if (adminToken) xhr.setRequestHeader('Authorization', `Bearer ${adminToken}`)

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText)
          if (xhr.status >= 200 && xhr.status < 300) resolve(data)
          else reject(new Error(data.error || 'Error al subir imagen'))
        } catch {
          reject(new Error('Respuesta inválida del servidor'))
        }
      }
      xhr.onerror = () => reject(new Error('Error de red'))
      xhr.send(formData)
    })
  },

  // Obtener imágenes adicionales de un producto
  async getImages(productId: string): Promise<ApiResponse<ProductImage[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/images`)
      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.getImages:', error)
      throw error
    }
  },

  // Eliminar imagen adicional
  async deleteImage(productId: string, imgId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/images/${imgId}`, {
        method: 'DELETE',
        headers: { ...getAdminAuthHeaders() },
      })
      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.deleteImage:', error)
      throw error
    }
  },

  // Marcar imagen como primaria
  async setPrimaryImage(productId: string, imgId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}/images/${imgId}/primary`, {
        method: 'PATCH',
        headers: { ...getAdminAuthHeaders() },
      })
      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.setPrimaryImage:', error)
      throw error
    }
  },

  // Actualizar producto
  async update(id: string, updates: Partial<Product>): Promise<ApiResponse<Product>> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(),
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al actualizar producto')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.update:', error)
      throw error
    }
  },

  // Eliminar producto
  async delete(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(),
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error al eliminar producto')
      }

      return await response.json()
    } catch (error) {
      console.error('Error en productsApi.delete:', error)
      throw error
    }
  }
}

